import { api } from "./api";
import {
  db,
  type CachedDebt,
  type CachedProduct,
  type CachedProductUnit,
  type LocalMoneyMovement,
} from "./db";

interface ServerProductUnit {
  id: string;
  name: string;
  multiplier: number | string;
  barcode?: string | null;
  salePrice?: number | string | null;
}

interface ServerProduct {
  id: string;
  name: string;
  sku: string;
  barcode?: string | null;
  baseUnit: string;
  businessVertical?: string | null;
  category?: string | null;
  subcategory?: string | null;
  salePrice: number | string;
  avgCost: number | string;
  stockQty: number | string;
  minStockQty: number | string;
  trackExpiry?: boolean;
  nextExpiryDate?: string | null;
  units?: ServerProductUnit[];
}

interface ClosingStateResponse {
  id: string;
  businessDate: string;
  systemCash: number;
  actualCash: number;
  discrepancy: number;
  reason?: string | null;
  state?: "OPEN" | "CLOSED" | "REOPENED";
  createdAt: string;
}

interface DashboardResponse {
  revenue: number;
  grossRevenue?: number;
  refundedRevenue?: number;
  orderCount: number;
  estimatedProfit: number | null;
  profitVisible?: boolean;
  accounts: Array<{
    id: string;
    name: string;
    type: "CASH" | "BANK";
    balance: number;
  }>;
  alerts: {
    lowStock: number;
    expiringLots?: number;
    pendingPayments: number;
  };
}

function normalizeUnit(unit: ServerProductUnit): CachedProductUnit {
  return {
    id: unit.id,
    name: unit.name,
    multiplier: Number(unit.multiplier),
    barcode: unit.barcode ?? null,
    salePrice: unit.salePrice == null ? null : Number(unit.salePrice),
  };
}

export async function refreshFromServer(): Promise<void> {
  if (!navigator.onLine) return;

  const [products, cash, dashboard, customers, suppliers, debts, closing] =
    await Promise.all([
      api<ServerProduct[]>("/products"),
      api<{ movements: Array<any>; accounts: Array<any> }>("/cash/movements"),
      api<DashboardResponse>("/dashboard"),
      api<Array<{ id: string; name: string; phone?: string | null }>>(
        "/customers",
      ),
      api<Array<{
        id: string;
        name: string;
        legalName?: string | null;
        phone?: string | null;
        email?: string | null;
        address?: string | null;
        taxCode?: string | null;
        bankName?: string | null;
        bankAccountNumber?: string | null;
        representativeName?: string | null;
        representativePhone?: string | null;
        note?: string | null;
      }>>("/suppliers"),
      api<CachedDebt[]>("/debts"),
      api<ClosingStateResponse | null>("/closing/latest"),
    ]);

  const normalizedProducts: CachedProduct[] = products.map((product) => ({
    id: product.id,
    name: product.name,
    sku: product.sku,
    barcode: product.barcode ?? null,
    baseUnit: product.baseUnit,
    businessVertical: product.businessVertical ?? null,
    category: product.category ?? null,
    subcategory: product.subcategory ?? null,
    salePrice: Number(product.salePrice),
    avgCost: Number(product.avgCost),
    stockQty: Number(product.stockQty),
    minStockQty: Number(product.minStockQty),
    trackExpiry: product.trackExpiry,
    nextExpiryDate: product.nextExpiryDate ?? null,
    units: (product.units ?? []).map(normalizeUnit),
  }));

  const movements: LocalMoneyMovement[] = cash.movements.map((movement) => ({
    id: movement.id,
    direction: movement.direction,
    amount: Number(movement.amount),
    category: movement.category ?? "Giao dịch",
    accountType: movement.account?.type === "BANK" ? "BANK" : "CASH",
    createdAt: movement.createdAt,
    syncStatus: "SYNCED",
  }));

  const normalizedDebts = debts.map((debt) => ({
    ...debt,
    balance: Number(debt.balance),
  }));

  await db.transaction(
    "rw",
    [db.products, db.money, db.customers, db.suppliers, db.meta],
    async () => {
      await db.products.clear();
      await db.products.bulkPut(normalizedProducts);
      await db.money.clear();
      await db.money.bulkPut(movements);
      await db.customers.clear();
      await db.customers.bulkPut(customers);
      await db.suppliers.clear();
      await db.suppliers.bulkPut(suppliers);
      await db.meta.bulkPut([
        { key: "dashboard", value: dashboard },
        { key: "debts", value: normalizedDebts },
        { key: "last-closing", value: closing },
      ]);
    },
  );
}
